import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessagesSquare, Globe2, Scale, FileSearch, Swords, Gauge, Users, Gavel, Handshake, GitCompare, Plane,
  Briefcase, Network, CalendarClock, BellRing, ShieldCheck, Lock, KeyRound, Mic, Sparkles, Flame, Target,
  ScrollText, Search, ChevronDown,
} from "lucide-react";

const GROUPS = [
  { name: "Research & answers", items: [
    [MessagesSquare, "AI Legal Assistant", "Ask anything in plain language. Answers are grounded in official statute feeds and live, cited web research, with a confidence badge on every reply."],
    [Globe2, "Deep Research", "Partner-grade research memos built from the live web, with every claim tied to a numbered, clickable source. Export to PDF or file into a matter."],
    [ShieldCheck, "Verify Law", "Check whether a statute or rule still says what you think it says, against EUR-Lex, Finlex, Lovdata and other official feeds."],
    [ScrollText, "Source Reasoning", "See exactly which authorities support a conclusion, and how strongly."],
  ]},
  { name: "Triage & strategy", items: [
    [Scale, "Do I need a lawyer?", "Instant triage of any situation: urgency, risk, what to do next, and when professional counsel is genuinely needed."],
    [Swords, "Strategy & Lawful Angles", "Every lawful route mapped by risk, cost and timing, so you choose the strongest path."],
    [Flame, "Risk Heatmap", "A visual map of where exposure concentrates across a deal, dispute or structure."],
    [Target, "Win Probability", "An evidence-weighted estimate of outcome likelihood, with the factors that move it."],
  ]},
  { name: "Litigation preparation", items: [
    [Users, "Opposing Counsel & Red Team", "Stress-test your position against the strongest arguments the other side will make."],
    [Gavel, "Judge Questions & Cross-Examination", "Rehearse the questions a court will ask and build disciplined cross-examination plans."],
    [Search, "Missing Evidence & Evidence Strength", "Find the gaps in your record and score the evidence you have."],
  ]},
  { name: "Contracts & documents", items: [
    [FileSearch, "Document Analysis", "Upload contracts and filings: risks, obligations, deadlines and clause rewrites, extracted in minutes."],
    [Handshake, "Negotiation Simulator", "Practise a negotiation against an AI counterpart that pushes back like a real one."],
    [Sparkles, "Explain for any audience", "Turn dense legal text into a clear briefing for a board, a client or a family member."],
  ]},
  { name: "Cross-border", items: [
    [GitCompare, "Jurisdiction Comparison", "Compare rules side by side across the US, UK, EU, Nordics, Canada, Australia and more."],
    [Plane, "Market Entry", "The legal checklist for expanding into a new country: entity, employment, tax and compliance touchpoints."],
  ]},
  { name: "Matters & monitoring", items: [
    [Briefcase, "Matter Workspace", "Files, tasks, timelines, parties and cited briefings for every matter, in one encrypted room."],
    [Network, "Authority Graph", "Every statute, ruling and source you have cited, mapped as an interactive 3D web."],
    [CalendarClock, "Smart Deadline Calendar", "Filing, limitation and hearing dates pulled automatically from chats, matters and documents."],
    [BellRing, "Law Change Alerts", "Watch any legal topic. Daily sweeps of courts, legislatures and regulators alert you the moment something changes."],
  ]},
  { name: "Security & access", items: [
    [Lock, "End-to-end encryption", "Conversations, matters and documents are encrypted at rest; your data is never used to train public models."],
    [KeyRound, "Two-factor authentication", "Authenticator-app 2FA, team roles and full audit logs for organisations."],
    [Mic, "Voice & installable app", "Dictate questions by voice and install Legalis on desktop or mobile as an app."],
  ]},
];

// Expandable atlas of every capability, for visitors who want the full detail.
export function FeatureAtlas() {
  const [open, setOpen] = useState(0);
  return (
    <section id="features" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="feature-atlas">
      <div className="grid lg:grid-cols-[380px_1fr] gap-12">
        <div className="lg:sticky lg:top-28 self-start">
          <div className="overline text-brand mb-4">Every capability</div>
          <h2 className="text-display text-4xl lg:text-5xl">One platform. The whole legal workflow.</h2>
          <p className="text-muted-foreground mt-5 leading-relaxed">From the first question to the final filing: research, strategy, preparation, documents and monitoring, all grounded in cited law.</p>
        </div>
        <div className="space-y-3">
          {GROUPS.map((g, gi) => (
            <div key={g.name} className="panel overflow-hidden">
              <button onClick={() => setOpen(open === gi ? -1 : gi)} className="w-full flex items-center justify-between px-6 py-5 text-left" data-testid={`atlas-group-${gi}`}>
                <span className="flex items-center gap-4"><span className="font-mono text-[11px] text-brand">{String(gi + 1).padStart(2, "0")}</span><span className="font-heading font-bold text-lg tracking-tight">{g.name}</span></span>
                <span className="flex items-center gap-3 text-xs text-muted-foreground">{g.items.length} tools <ChevronDown className={`w-4 h-4 transition-transform ${open === gi ? "rotate-180 text-brand" : ""}`} /></span>
              </button>
              <AnimatePresence initial={false}>
                {open === gi && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }}>
                    <div className="grid md:grid-cols-2 gap-3 px-6 pb-6">
                      {g.items.map(([Icon, t, d]) => (
                        <div key={t} className="rounded-xl border border-white/[0.05] bg-white/[0.015] p-5" data-testid="atlas-item">
                          <Icon className="w-[18px] h-[18px] text-brand mb-3" strokeWidth={1.6} />
                          <div className="font-semibold">{t}</div>
                          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{d}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const FAQ = [
  ["Is Legalis AI a law firm?", "No. Legalis AI provides AI-generated legal information grounded in official sources and live cited research. It does not give legal advice; for decisions with real consequences, it tells you when to involve a qualified lawyer and can refer you to vetted firms."],
  ["Which jurisdictions are covered?", "Official statute feeds cover the United States, United Kingdom, European Union (EUR-Lex), Finland (Finlex), Norway, Canada and Australia, with research support for further jurisdictions including Sweden, Denmark, Germany and France."],
  ["How do you prevent AI hallucinations?", "Every answer is grounded in retrieved primary law and live web research, with numbered citations you can click. A confidence badge shows whether an answer is verified against official statutes, web-corroborated, or reasoning-only."],
  ["Is my data private?", "Conversations, matters and documents are encrypted at rest, protected by optional two-factor authentication, and never used to train public AI models."],
  ["Who is it for?", "Founders and SMEs, high-net-worth individuals and family offices handling cross-border questions, and law firms or in-house teams that want faster, cited research and preparation."],
];

export function LandingFAQ() {
  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-24" data-testid="landing-faq">
      <div className="overline text-brand mb-4">Questions</div>
      <h2 className="text-display text-4xl mb-10">What clients ask first.</h2>
      <div className="space-y-3">
        {FAQ.map(([q, a]) => (
          <details key={q} className="lux-card p-6" data-testid="faq-item"><summary className="font-heading font-bold tracking-tight cursor-pointer list-none">{q}</summary><p className="text-muted-foreground mt-3 leading-relaxed">{a}</p></details>
        ))}
      </div>
    </section>
  );
}
